import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiTarget,
  FiPlay,
  FiBook,
  FiCalendar,
  FiActivity,
  FiFileText,
  FiVideo,
  FiMessageCircle,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import Tutorial from './Tutorial';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import { authAPI, userStatsAPI, bookingAPI } from '../services/api';
import './Dashboard.css';

interface DashboardProps {
  onLogout?: () => void;
  userEmail?: string;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout, userEmail }) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: ''
  });
  const { showTutorial, currentTutorialStep, startTutorial, completeTutorial, skipTutorial } = useTutorial();
  const [tutorialChecked, setTutorialChecked] = useState(false);
  const [userStats, setUserStats] = useState({
    completed_segments: 0,
    completed_podcasts: 0,
    dialogues_practiced: 0,
    mock_tests_submitted: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<Array<{ type: string; name: string; date: string; icon: IconType; detail?: string | null }>>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [hasBookingServices, setHasBookingServices] = useState(false);

  const stats: Array<{ label: string; value: string; icon: IconType; color: string }> = useMemo(
    () => [
      {
        label: 'Dialogues practiced',
        value: statsLoading ? '…' : userStats.dialogues_practiced.toString(),
        icon: FiMessageCircle,
        color: '#6f42c1',
      },
      {
        label: 'Segments practiced',
        value: statsLoading ? '…' : userStats.completed_segments.toString(),
        icon: FiFileText,
        color: '#28a745',
      },
      {
        label: 'Podcasts watched',
        value: statsLoading ? '…' : userStats.completed_podcasts.toString(),
        icon: FiVideo,
        color: '#ffc107',
      },
      {
        label: 'Mock tests submitted',
        value: statsLoading ? '…' : userStats.mock_tests_submitted.toString(),
        icon: FiTarget,
        color: '#17a2b8',
      },
    ],
    [statsLoading, userStats]
  );

  const quickActions: Array<{ title: string; icon: IconType; path: string; color: string }> = useMemo(() => {
    const actions: Array<{ title: string; icon: IconType; path: string; color: string }> = [
      { title: 'Test rules', icon: FiPlay, path: '/test-rules', color: '#E63946' },
      { title: 'Dialogue categories', icon: FiMessageCircle, path: '/material/dialogue-categories', color: '#6f42c1' },
      { title: 'Structural segments', icon: FiBook, path: '/material/structured-segments', color: '#6c757d' },
      { title: 'Podcast', icon: FiVideo, path: '/material/podcast', color: '#ffc107' },
      { title: 'Mock Service', icon: FiTarget, path: '/mock-service', color: '#17a2b8' },
    ];
    if (hasBookingServices) {
      actions.push({ title: 'Book a session', icon: FiCalendar, path: '/booking', color: '#28a745' });
    }
    return actions;
  }, [hasBookingServices]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  const applyProfileToState = useCallback(
    (profile: Awaited<ReturnType<typeof authAPI.getProfile>>) => {
      setUserInfo({
        email: profile.email || userEmail || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
      });
    },
    [userEmail]
  );

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const profile = await authAPI.getProfile();
          applyProfileToState(profile);

          if (!profile.is_staff && !profile.is_superuser && !tutorialChecked) {
            setTutorialChecked(true);
            if (!profile.tutorial_completed) {
              setTimeout(() => {
                startTutorial(0, '/dashboard');
              }, 500);
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    fetchUserProfile();
  }, [userEmail, tutorialChecked, applyProfileToState, startTutorial]);

  const refreshUserProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const profile = await authAPI.getProfile();
      applyProfileToState(profile);
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, [applyProfileToState]);

  useEffect(() => {
    // Fetch user statistics
    const fetchUserStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await userStatsAPI.getUserStats();
        setUserStats({
          completed_segments: stats.completed_segments ?? 0,
          completed_podcasts: stats.completed_podcasts ?? 0,
          dialogues_practiced: stats.dialogues_practiced ?? 0,
          mock_tests_submitted: stats.mock_tests_submitted ?? 0,
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchUserStats();
  }, []);

  useEffect(() => {
    let cancelled = false;
    bookingAPI
      .listServices()
      .then((services) => {
        if (!cancelled) setHasBookingServices(Array.isArray(services) && services.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasBookingServices(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Fetch recent activities
    const fetchRecentActivities = async () => {
      try {
        setActivitiesLoading(true);
        const activities = await userStatsAPI.getRecentActivity();
        
        // Map activities to format with icons
        const mappedActivities = activities.map(activity => {
          let icon: IconType = FiActivity;
          if (activity.activity_type === 'test') {
            icon = FiCheckCircle;
          } else if (activity.activity_type === 'podcast') {
            icon = FiVideo;
          } else if (activity.activity_type === 'material') {
            icon = FiBook;
          } else if (activity.activity_type === 'dialogue') {
            icon = FiMessageCircle;
          } else if (activity.activity_type === 'mock') {
            icon = FiTarget;
          }
          
          return {
            type: activity.type,
            name: activity.name,
            date: activity.date,
            icon,
            detail: activity.detail ?? (activity.practice_attempt_number
              ? `Practice #${activity.practice_attempt_number}`
              : null),
          };
        });
        
        setRecentActivities(mappedActivities);
      } catch (error) {
        console.error('Error fetching recent activities:', error);
        setRecentActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    };
    fetchRecentActivities();
  }, []);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const handleTutorialComplete = async () => {
    completeTutorial();
  };

  const handleTutorialSkip = async () => {
    skipTutorial();
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={handleLogout}
          onProfileUpdated={refreshUserProfile}
        />
        
        <div className="dashboard-content" data-tutorial-target="dashboard">
          <div className="dashboard-content-inner">
          <motion.div
            className="dashboard-welcome"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <h1 className="dashboard-title">
                Welcome Back{userInfo.first_name ? `, ${userInfo.first_name}` : ''}
              </h1>
              <p className="dashboard-subtitle">Continue your Persian CCL learning journey</p>
            </div>
          </motion.div>

        <motion.div
          className="dashboard-stats"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon as React.ComponentType<{ size?: number; color?: string }>;
            return (
              <motion.div
                key={index}
                className="stat-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="stat-icon" style={{ color: stat.color }}>
                  <IconComponent size={28} />
                </div>
                <div className="stat-info">
                  <div className="stat-value">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          className="quick-actions"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="section-title">Quick Actions</h2>
          <div className="actions-grid">
            {quickActions.map((action, index) => {
              const IconComponent = action.icon as React.ComponentType<{ size?: number; color?: string }>;
              return (
                <motion.div
                  key={index}
                  className="action-card"
                  variants={itemVariants}
                  whileHover={{ scale: 1.03, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(action.path)}
                >
                  <div className="action-icon" style={{ backgroundColor: `${action.color}20`, color: action.color }}>
                    <IconComponent size={32} />
                  </div>
                  <div className="action-title">{action.title}</div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          className="recent-activity"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="section-title">Recent Activity</h2>
          <div className="activity-list">
            {activitiesLoading ? (
              <div style={{ color: '#999999', textAlign: 'center', padding: '20px' }}>Loading activities...</div>
            ) : recentActivities.length === 0 ? (
              <div style={{ color: '#999999', textAlign: 'center', padding: '20px' }}>No recent activity</div>
            ) : (
              recentActivities.map((activity, index) => {
              const IconComponent = activity.icon as React.ComponentType<{ size?: number; color?: string }>;
              return (
                <motion.div
                  key={index}
                  className="activity-item"
                  variants={itemVariants}
                  whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                >
                  <div className="activity-icon">
                    <IconComponent size={20} />
                  </div>
                  <div className="activity-info">
                    <div className="activity-type">{activity.type}</div>
                    <div className="activity-name">{activity.name}</div>
                    {activity.detail && (
                      <div className="activity-detail">{activity.detail}</div>
                    )}
                  </div>
                  <div className="activity-date">{activity.date}</div>
                </motion.div>
              );
              })
            )}
          </div>
        </motion.div>
          </div>
        </div>
      </div>
      
      {showTutorial && (currentTutorialStep === 0 || currentTutorialStep === 1) && (
        <Tutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
          initialStep={currentTutorialStep}
        />
      )}
    </div>
  );
};

export default Dashboard;
